<template>
  <div class="reset-password-form">
    <!-- Error Alert -->
    <div v-if="hasError" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle"></i>
      {{ errorMessage }}
    </div>

    <!-- Success Alert -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      <i class="fas fa-check-circle"></i>
      {{ successMessage }}
    </div>

    <form
      v-if="!isTokenValid"
      @submit.prevent="validateToken"
      class="auth-form"
    >
      <div class="form-group">
        <input
          id="token"
          v-model="token"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.token }"
          placeholder="Enter your reset token"
          required
          :disabled="isLoading"
        />
        <div v-if="errors.token" class="invalid-feedback">
          {{ errors.token[0] }}
        </div>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="loading-spinner"></span>
        {{ isLoading ? "Validating Token..." : "Validate Token" }}
      </button>
    </form>

    <form v-else @submit.prevent="handleSubmit" class="auth-form">
      <div class="form-group">
        <div class="password-input-wrapper">
          <input
            id="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control"
            :class="{ 'is-invalid': errors.password }"
            placeholder="Enter your new password"
            required
            :disabled="isLoading"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showPassword = !showPassword"
            :disabled="isLoading"
          >
            <i :class="showPassword ? 'fi fi-eye-slash' : 'fi fi-eye'"></i>
          </button>
        </div>
        <div v-if="errors.password" class="invalid-feedback">
          {{ errors.password[0] }}
        </div>
        <!-- Password Requirements Checklist -->
        <div class="password-requirements">
          <div class="requirement" :class="{ met: passwordMeetsLength }">
            <i
              :class="passwordMeetsLength ? 'fi fi-check' : 'fi fi-circle'"
            ></i>
            <span>At least 8 characters</span>
          </div>
          <div class="requirement" :class="{ met: passwordMeetsUppercase }">
            <i
              :class="passwordMeetsUppercase ? 'fi fi-check' : 'fi fi-circle'"
            ></i>
            <span>One uppercase letter</span>
          </div>
          <div class="requirement" :class="{ met: passwordMeetsLowercase }">
            <i
              :class="passwordMeetsLowercase ? 'fi fi-check' : 'fi fi-circle'"
            ></i>
            <span>One lowercase letter</span>
          </div>
          <div class="requirement" :class="{ met: passwordMeetsNumber }">
            <i
              :class="passwordMeetsNumber ? 'fi fi-check' : 'fi fi-circle'"
            ></i>
            <span>One number</span>
          </div>
          <div class="requirement" :class="{ met: passwordMeetsSpecial }">
            <i
              :class="passwordMeetsSpecial ? 'fi fi-check' : 'fi fi-circle'"
            ></i>
            <span>One special character</span>
            <div class="tooltip-container">
              <i class="fi fi-info-circle tooltip-icon"></i>
              <div class="tooltip-content">
                Allowed special characters: @ $ ! % * ? & #
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="password-input-wrapper">
          <input
            id="password_confirmation"
            v-model="password_confirmation"
            :type="showConfirmPassword ? 'text' : 'password'"
            class="form-control"
            :class="{ 'is-invalid': errors.password_confirmation }"
            placeholder="Confirm your new password"
            required
            :disabled="isLoading"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showConfirmPassword = !showConfirmPassword"
            :disabled="isLoading"
          >
            <i
              :class="showConfirmPassword ? 'fi fi-eye-slash' : 'fi fi-eye'"
            ></i>
          </button>
        </div>
        <div v-if="errors.password_confirmation" class="invalid-feedback">
          {{ errors.password_confirmation[0] }}
        </div>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="loading-spinner"></span>
        {{ isLoading ? "Resetting Password..." : "Reset Password" }}
      </button>
    </form>

    <div class="form-footer">
      <p class="signin-link">
        Remember your password?
        <router-link :to="`/${currentLocale}/auth/login`" class="link-primary">
          Sign in here
        </router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AuthUtils from "../../utils/auth.utils";

export default {
  name: "ResetPassword",
  data() {
    return {
      token: "",
      password: "",
      password_confirmation: "",
      showPassword: false,
      showConfirmPassword: false,
      errors: {},
      successMessage: "",
      isTokenValid: false,
      currentLocale: AuthUtils.getCurrentLocale(),
    };
  },

  computed: {
    ...mapGetters(["isLoading", "hasError", "getError"]),

    errorMessage() {
      const storeError = this.$store.getters.getError;
      if (storeError) return storeError;

      // Handle validation errors
      if (Object.keys(this.errors).length > 0) {
        const firstError = Object.values(this.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }

      return "";
    },

    // Password requirement checks
    passwordMeetsLength() {
      return this.password.length >= 8;
    },
    passwordMeetsUppercase() {
      return /[A-Z]/.test(this.password);
    },
    passwordMeetsLowercase() {
      return /[a-z]/.test(this.password);
    },
    passwordMeetsNumber() {
      return /\d/.test(this.password);
    },
    passwordMeetsSpecial() {
      return /[@$!%*?&#]/.test(this.password);
    },
  },

  methods: {
    ...mapActions(["validateResetToken", "resetPassword"]),

    async validateToken() {
      this.errors = {};
      this.successMessage = "";

      // Sanitize token input
      const sanitizedToken = this.token.trim();

      if (!sanitizedToken) {
        this.errors = {
          token: ["Token is required"],
        };
        return;
      }

      try {
        await this.$store.dispatch("validateResetToken", sanitizedToken);
        this.isTokenValid = true;
      } catch (error) {
        if (error.status === 422 && error.errors) {
          this.errors = error.errors;
        }
      }
    },

    async handleSubmit() {
      this.errors = {};
      this.successMessage = "";

      // Sanitize password inputs
      const sanitizedPassword = this.password.trim();
      const sanitizedPasswordConfirmation = this.password_confirmation.trim();

      // Basic validation
      if (
        !this.validateForm(sanitizedPassword, sanitizedPasswordConfirmation)
      ) {
        return;
      }

      try {
        await this.$store.dispatch("resetPassword", {
          token: this.token.trim(),
          password: sanitizedPassword,
          password_confirmation: sanitizedPasswordConfirmation,
        });
        this.successMessage =
          "Password has been reset successfully. Redirecting to login...";

        // Redirect after successful reset
        setTimeout(() => {
          this.$router.push(`/${this.currentLocale}/auth/login`);
        }, 2000);
      } catch (error) {
        if (error.status === 422 && error.errors) {
          this.errors = error.errors;
        }
      }
    },

    validateForm(password, passwordConfirmation) {
      const errors = {};

      if (!password) {
        errors.password = ["Password is required"];
      } else if (password.length < 8) {
        errors.password = ["Password must be at least 8 characters"];
      } else if (
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
          password
        )
      ) {
        errors.password = [
          "Password must contain at least one uppercase letter, one lowercase letter, one number and one special character",
        ];
      }

      if (!passwordConfirmation) {
        errors.password_confirmation = ["Password confirmation is required"];
      } else if (password !== passwordConfirmation) {
        errors.password_confirmation = ["Passwords do not match"];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
  },

  created() {
    // Check if user is already authenticated
    if (AuthUtils.isAuthenticated()) {
      AuthUtils.redirectAfterLogin();
    }

    // Check if token is provided in URL
    const token = this.$route.query.token;
    if (token) {
      this.token = token;
      this.validateToken();
    }
  },
};
</script>

<style scoped>
.reset-password-form {
  width: 100%;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: none;
  font-size: 0.9rem;
}

.alert-danger {
  background-color: #fee;
  color: #c53030;
}

.alert-success {
  background-color: #f0fff4;
  color: #38a169;
}

.alert i {
  margin-right: 0.5rem;
}

.auth-form {
  width: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: left;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #fff;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #e53e3e;
}

.form-control:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.password-input-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #333;
}

.password-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.invalid-feedback {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.btn {
  padding: 0.875rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.btn-block {
  width: 100%;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.form-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.signin-link {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.link-primary {
  color: #2c3e50;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.link-primary:hover {
  color: #34495e;
  text-decoration: underline;
}

.password-requirements {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #666;
  text-align: left;
}

.requirement {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  transition: all 0.2s ease;
}

.requirement i {
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.requirement.met {
  color: #38a169;
}

.requirement.met i {
  color: #38a169;
}

.requirement:not(.met) i {
  color: #a0aec0;
}

.tooltip-container {
  position: relative;
  display: inline-block;
  margin-left: 0.5rem;
}

.tooltip-icon {
  color: #666;
  cursor: help;
  font-size: 0.9rem;
}

.tooltip-content {
  visibility: hidden;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  text-align: center;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;
}

.tooltip-container:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}
</style>
